// @flow

import React, { Component } from 'react';
import type { Element } from 'react';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Link } from 'react-router-dom';
import { connector } from '../store';
import type { VideoData } from '../store';
import DescriptionOverlay from './DescriptionOverlay.jsx';
import { getBasicVideoTrackingLabel, getVideoTrackingLabel, getPlayingVideoById, videoDataToPlayerData } from '../helpers';
import Loadable from 'react-loadable';
import ScrollTo from './ScrollTo.jsx';
import VideoEndScreen from './VideoEndScreen.jsx';
import { PassportSignUpBanner, PassportActivationBanner, TermsBanner } from './VideoPlaceholder.jsx';
import PBSPlayer from './PBSPlayer.jsx';
import LivestreamPlayer from './LivestreamPlayer.jsx';
import YouTubePlayer from './YouTubePlayer.jsx';
import UStreamPlayer from './UStreamPlayer.jsx';
import VimeoPlayer from './VimeoPlayer.jsx';
import PBSLivestreamPlayer from './PBSLivestreamPlayer.jsx';
import flag from './../../Feature/Feature';

import PBSPartnerPlayer from '@twincitiespublictelevision/pbs-partner';
import Core from '../../Core/Core';
import ga from '../../../vendor/google-analytics';
import YoutTubeLivePlayer from "./YouTubeLivePlayer";

let user = Core.user;

let AsyncClipBar = Loadable({
  loader: () => import(/* webpackChunkName: "Show.ClipBar" */ './ClipBar.jsx').then(dep => dep.default),
  loading: () => null
});

let AsyncSocialBar = Loadable({
  loader: () => import(/* webpackChunkName: "Show.VideoPlayerSocialBar" */ './VideoPlayerSocialBar.jsx').then(dep => dep.default),
  loading: () => null
});

let AsyncStoryboard = Loadable({
  loader: () => import(/* webpackChunkName: "Show.StoryboardPost" */ './StoryboardPost.jsx').then(dep => dep.default),
  loading: () => null
});

function formatAirdate(timestamp) {
  let date = new Date();

  // Video airdates do not carry timezones, so the server sends a UTC version.
  // The client offset is applied here to reverse the native offset effects of
  // the get month / date / year functions
  date.setTime(timestamp * 1000);
  date.setTime(date.getTime() + (date.getTimezoneOffset() * 60 * 1000));

  let year = (date.getFullYear() + '').substr(-2);

  return `${(date.getMonth() + 1)}/${date.getDate()}/${year}`;
}

export class VideoPlayer extends Component<*, *> {
  trackingCategory: string;
  player: PBSPartnerPlayer;
  loadVideo: Function;
  state: {
    hoverPlayer: boolean,
    hoverTimeout: ?number
  };

  constructor(props: *) {
    super(props);
    this.trackingCategory = 'VideoPlayer';
    this.loadVideo = this.loadVideo.bind(this);
    this.state = {
      hoverPlayer: false,
      hoverTimeout: null
    };
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  componentWillReceiveProps(newProps: *) {
    if (this.player && this.props.player.currentlyPlaying.id !== newProps.player.currentlyPlaying.id) {
      this.player.destroy();
      this.player = null;
    }
  }

  shouldComponentUpdate(nextProps: *) {

    // Skip rendering entirely if there is nothing to load into the player
    return !!nextProps.player.currentlyPlaying.id;
  }

  loadVideo(player: HTMLIFrameElement) {

    // Do nothing with null calls when re-rendering
    if (!player) { return; }

    let video = getPlayingVideoById(this.props.player.container, this.props.player.currentlyPlaying.id);

    // Do nothing when transitioning from one video to the next (ie. the time
    // when no video is loaded). Or if for some reason a video to display can
    // not be selected (this should never occur outside of transitions
    if (!video) { return; }

    if (!this.player) {
      window.player = this.player = new PBSPartnerPlayer({});
    }

    this.player.setPlayer(player);

    this.player.on('play', this.props.clickPlay);
    this.player.on('pause', this.props.clickPause);
    this.player.on('seek', this.props.clickSeek);
    this.player.on('MediaStop', this.props.clickStop);

    let currentId = this.props.player.currentlyPlaying.id,
      fullLength = this.props.player.container.fullLength,
      preview = this.props.player.container.preview,
      isFullLength = fullLength && fullLength.id === currentId,
      isPreview = preview && preview.id === currentId,
      isClip = !isFullLength && !isPreview;

    if (isFullLength) {
      let handler = () => {
        requestAnimationFrame(() => {
          this.props.videoEnd(currentId);
        });

        this.player.off('complete', handler);
      };

      // MediaStart is triggered when the video first plays as well as each
      // replay. It is rebound each time to account for being unbound at the
      // end of each video
      this.player.on('MediaStart', () => {
        this.player.on('complete', handler);
      });
    }

    if (isPreview) {
      let endOfPreview = this.props.endOfPreview;
      this.player.on('complete', () => {

        // Force the next video to transition asynchronously to allow
        // Chrome | Safari fix to fully execute
        requestAnimationFrame(() => {
          endOfPreview(currentId, this.props.history)
        });
      });
    }

    if (isClip) {
      let endOfClip = this.props.endOfClip;
      this.player.on('complete', () => {

        // Force the next video to transition asynchronously to allow
        // Chrome | Safari fix to fully execute
        requestAnimationFrame(() => {
          endOfClip(currentId, this.props.history);
        });
      });
    }

    // Try to generate a tracking label
    let trackingLabel = getBasicVideoTrackingLabel(this.props.show, this.props.player);

    // If successful then create bindings to the media events
    if (trackingLabel) {
      this.player.analytics.setTrackingFunction(ga);

      this.player.analytics.addMediaTracking(
        this.trackingCategory,
        trackingLabel,
        'metric1',
        {
          start: 'MediaStart | [A]',
          stop: 'MediaStop | [A]'
        }
      );
    }
  }

  wrapAction(action: Function) {
    return function (event: Event) {
      event.preventDefault();
      action();
    }
  }

  startHoverPlayer() {
    if (!this.props.player.livestreamOverride) {
      if (this.state.hoverTimeout) {
        clearTimeout(this.state.hoverTimeout);
      }

      this.setState({
        hoverPlayer: true,
        hoverTimeout: null
      });
    }
  }

  stopHoverPlayer() {
    if (this.state.hoverTimeout) {
      clearTimeout(this.state.hoverTimeout);
    }

    let timeout = setTimeout(() => {
      if (this.state.hoverTimeout) {
        clearTimeout(this.state.hoverTimeout);
      }

      this.setState({
        hoverPlayer: false,
        hoverTimeout: null
      });
    }, 2000);

    this.setState({
      hoverTimeout: timeout
    });
  }

  hasLoadedVideo() {
    return !!this.props.player.currentlyPlaying.id;
  }

  isFullLengthLoaded() {
    return this.hasLoadedVideo() &&
      this.props.player.container.fullLength &&
      this.props.player.container.fullLength.id === this.props.player.currentlyPlaying.id;
  }

  isPreviewLoaded() {
    return this.hasLoadedVideo() &&
      this.props.player.container.preview &&
      this.props.player.container.preview.id === this.props.player.currentlyPlaying.id;
  }

  isClipLoaded() {
    return this.hasLoadedVideo() &&
      !this.isFullLengthLoaded() &&
      !this.isPreviewLoaded();
  }

  isClipbarRequired() {
    let { fullLength, preview, clips } = this.props.player.container;

    // Compute the minimum clips required for the clip bar based on the presence of full length and preview videos
    let minClips = fullLength || preview ? 1 : 2;

    return clips.length >= minClips;
  }

  renderFullLengthHoverControl() {
    let fullLength = this.props.player.container.fullLength;

    return fullLength && <div
      className={'return-to-episode' + (!this.isFullLengthLoaded() && this.state.hoverPlayer ? ' active' : '')}
      onClick={() => {
        let label = getVideoTrackingLabel(this.props.show, this.props.player, fullLength.id);

        if (label) {
          ga('send', 'event', this.trackingCategory + ' | Return To Episode', 'Click Overlay Return | [A]', label);
        }

        this.props.loadPlayerData(videoDataToPlayerData(fullLength, false), this.props.history);
      }}>
      Watch Episode
    </div>;
  }

  renderVideoPlaceholder(video: VideoData): Element<any> {
    let userHasAccess = user.getAccessLevel() >= video.minimumPassportLevel;

    if (!userHasAccess) {
      if (user.isSignedIn()) {
        return <PassportActivationBanner src={video.mezzanine} />;
      } else {
        return <PassportSignUpBanner src={video.mezzanine} />;
      }
    }

    if (flag('vppa') && video.minimumPassportLevel > 0 && !user.hasAcceptedVPPA()) {
      return <TermsBanner src={video.mezzanine} />;
    }

    return <div></div>;
  }

  renderLivePlayer(serviceName: string) {
    switch (serviceName) {
      case 'livestream':
        return <LivestreamPlayer
          account={this.props.show.livestream.service.account}
          embed={this.props.show.livestream.service.embed}
          autoplay={true} />;
      case 'youtube':
        return <YouTubePlayer
          embed={this.props.show.livestream.service.embed}
          autoplay={true} />;
      case 'youtubelive':
        return <YoutTubeLivePlayer
          embed={this.props.show.livestream.service.embed}
          autoplay={true}
        />;
      case 'ustream':
        return <UStreamPlayer
          embed={this.props.show.livestream.service.embed}
          autoplay={true} />;
      case 'vimeo':
        return <VimeoPlayer
          embed={this.props.show.livestream.service.embed}
          autoplay={true} />;
      case 'pbslive':
        return <PBSLivestreamPlayer autoplay={true} />;
      default:
        return '';
    }
  }

  renderLivestreamMetaData() {
    return (
      <div>
        <div className="video-title headline-section">
          {this.props.show.livestream.service.title}
          <div className="body-small">
            <span className="live-indicator">LIVE</span>
          </div>
        </div>
        <p className="video-description body-normal">
          {this.props.show.livestream.service.description}
        </p>
      </div>
    );
  }

  renderVideoMetaData(video: ?VideoData) {
    if (this.props.player.livestreamOverride) {
      return this.renderLivestreamMetaData();
    } else {
      if (typeof video === 'undefined' || video === null) {
        return <div></div>;
      }

      let displayableSeason = video.season ?
        <div className="season-info body-small">
          {this.seasonEpisodeDescriptor(video.season, video.episodeWithinSeason)}
        </div> : '';

      let title = window.location.pathname.search(/\/video\//) !== -1 ? <h1 className="headline-section">{video.title}</h1> : <h2 className="headline-section">{video.title}</h2>;

      return (
        <div>
          <div className="video-title headline-section">
            {title}
            {
              video.displaySeason &&
              <CSSTransition
                classNames="react-fade-in"
                appear={true}
                timeout={{ enter: 250, leave: 0 }}
                in={true}>
                <div>
                  {displayableSeason}
                </div>
              </CSSTransition>
            }
          </div>
          <p className="video-description show-for-xlarge-up body-normal">
            {video.shortDescription}
            <CSSTransition
              classNames="react-fade-in"
              appear={true}
              timeout={{ enter: 250, leave: 0 }}
              in={true}>
              <span>
                <button className="action-text" onClick={() => {
                  let label = getBasicVideoTrackingLabel(this.props.show, this.props.player);

                  if (label) {
                    ga('send', 'event', this.trackingCategory + ' | Extended Desc', 'Display | [P]', label);
                  }

                  this.props.clickDescriptionMore();
                }}>&nbsp;More</button>
              </span>
            </CSSTransition>
          </p>
          <p className="extended-description hide-for-xlarge-up" dangerouslySetInnerHTML={{ __html: video.extendedDescription }} />

          <CSSTransition
            classNames="react-fade-in"
            appear={true}
            timeout={{ enter: 250, leave: 0 }}
            in={true}>
            <p>
              <span><b>AIR DATE</b> {formatAirdate(video.airdate)}</span>
            </p>
          </CSSTransition>
        </div>
      );
    }
  }

  renderVideoControls() {
    let { fullLength, preview } = this.props.player.container;
    let liveCtrl = '', epsCtrl = '', clipCtrl = '', previewCtrl = '';

    if (!this.props.player.livestreamOverride && this.props.show.livestream.isLive) {
      liveCtrl = <Link to={`/${this.props.show.slug}/`}>
        <button className="video-control-btn call-to-action-btn" onClick={this.props.loadLivestream}>Watch Live</button>
      </Link>;
    }

    if (!this.props.player.livestreamOverride) {
      if (fullLength && !this.isFullLengthLoaded()) {
        let epsHandler = this.wrapAction(() => {
          this.props.loadPlayerData(
            videoDataToPlayerData(fullLength),
            this.props.history
          );
        });

        epsCtrl = <button className="video-control-btn call-to-action-btn" onClick={(e) => {
          let label = getVideoTrackingLabel(this.props.show, this.props.player, fullLength.id);

          if (label) {
            ga('send', 'event', this.trackingCategory + ' | Return To Episode', 'Click Return | [A]', label);
          }

          epsHandler(e);
        }}>Watch Episode</button>;
      }

      if (this.isClipbarRequired()) {
        let clipHndlr = this.props.player.clipBarState.open ? this.props.clickClipsOff : this.props.clickClipsOn;
        let clipClass = 'video-control-btn clip-toggle call-to-action-btn';

        if (this.props.player.clipBarState.open) {
          clipClass += ' active';
        }

        clipCtrl = <button className={clipClass} onClick={this.wrapAction(clipHndlr)}>Clips</button>;
      }

      if (preview && !this.isPreviewLoaded()) {
        let previewClass = 'video-control-btn preview-toggle call-to-action-btn';

        previewCtrl = <button className={previewClass} onClick={() => {
          let label = getVideoTrackingLabel(this.props.show, this.props.player, preview.id);

          if (label) {
            ga('send', 'event', this.trackingCategory + ' | Preview', 'Click Preview | [A]', label);
          }

          this.props.clickPreviewOn(this.props.history);
        }}>Watch Preview</button>;
      }
    }

    return (liveCtrl || epsCtrl || previewCtrl || clipCtrl) &&
      <CSSTransition
        classNames="react-fade-in"
        appear={true}
        timeout={{ enter: 250, leave: 0 }}
        in={true}>
        <div className="row video-control-btns">
          <div className="small-12 columns">
            {liveCtrl} {epsCtrl} {previewCtrl} {clipCtrl}
          </div>
        </div>
      </CSSTransition>;
  }

  seasonEpisodeDescriptor(season: number, episodeWithinSeason: ?number): string {
    if (typeof episodeWithinSeason !== 'undefined' && episodeWithinSeason !== null) {
      return `Season ${season} Episode ${episodeWithinSeason}`;
    }

    return `Season ${season}`;
  }

  getSocialUrl(video: ?VideoData): string {
    if (this.props.player.livestreamOverride || !video) {
      return `${window.location.origin}/${this.props.show.slug}/`;
    } else {
      return `${window.location.origin}/${this.props.show.slug}/video/${video.slug}`;
    }
  }

  render() {
    let loadedVideo = getPlayingVideoById(this.props.player.container, this.props.player.currentlyPlaying.id);
    let userHasAccess = loadedVideo &&
      user.getAccessLevel() >= loadedVideo.minimumPassportLevel &&
      (!flag('vppa') || user.hasAcceptedVPPA() || loadedVideo.minimumPassportLevel === 0);

    return (
      <div className="player row">
        <ScrollTo
          history={this.props.history}
          path={new RegExp(`(/${this.props.show.slug}/$|/video/)`)}
        />
        <div className="small-12 columns">
          <div className="featured-wrapper">
            <div className="row">
              <div className="small-12 xlarge-8 video-player-row columns">
                <div className="video-player-wrap"
                  onMouseEnter={this.startHoverPlayer.bind(this)}
                  onMouseLeave={this.stopHoverPlayer.bind(this)}
                  key={loadedVideo && loadedVideo.id || ''}>
                  {loadedVideo && this.renderVideoPlaceholder(loadedVideo)}
                  {!this.props.player.livestreamOverride && userHasAccess && loadedVideo &&
                    <PBSPlayer
                      refCallback={this.loadVideo}
                      tpMediaId={loadedVideo.tpMediaId}
                      uid={user.getUid()}
                      autoplay={this.props.player.currentlyPlaying.autoplay} />
                  }
                  {this.props.player.livestreamOverride && this.renderLivePlayer(this.props.show.livestream.service.name)}
                  {this.renderFullLengthHoverControl()}
                  <div className="end-screen-wrapper">
                    {this.props.player.endScreenOpen && this.props.player.endScreen.nextVideo && this.props.player.endScreen.nextVideo.fullLength &&
                      <VideoEndScreen try_autoplay={true} show={this.props.show} video={this.props.player.endScreen.nextVideo.fullLength} history={this.props.history} />
                    }
                  </div>
                </div>
              </div>
              <div className="small-12 xlarge-4 columns">
                {this.renderVideoMetaData(loadedVideo)}
                {this.renderVideoControls()}

                {(this.props.player.livestreamOverride || loadedVideo) && <CSSTransition
                  classNames="react-fade-in"
                  appear={true}
                  timeout={{ enter: 250, leave: 0 }}
                  in={true}>
                  <div className="row show-for-xlarge-up">
                    <div className="small-12 columns">
                      {<AsyncSocialBar
                        baseUrl={this.getSocialUrl(loadedVideo)}
                        title={this.props.player.livestreamOverride ? this.props.show.livestream.service.title : loadedVideo && loadedVideo.title}
                        content={this.props.player.livestreamOverride ? this.props.show.livestream.service.description : loadedVideo && loadedVideo.shortDescription}
                        fb={{ tracking: { action: 'facebook/player' } }}
                        tw={{ tracking: { action: 'twitter/player' } }}
                        em={{ tracking: { action: 'email/player' } }}
                        embed={{ tracking: { action: 'embed/player' } }}
                        tpMediaId={loadedVideo && loadedVideo.tpMediaId}
                      />}
                    </div>
                  </div>
                </CSSTransition>}

                {this.props.show.storyboard && <CSSTransition
                  classNames="react-fade-in"
                  appear={true}
                  timeout={{ enter: 250, leave: 0 }}
                  in={true}
                  className="related-storyboard">
                  <div>
                    <AsyncStoryboard />
                  </div>
                </CSSTransition>}
              </div>
            </div>

            <div className="row">
              {this.props.player.moreVideoOverlayOpen &&
                <DescriptionOverlay video={loadedVideo} clickClose={() => {
                  let label = getBasicVideoTrackingLabel(this.props.show, this.props.player);

                  if (label) {
                    ga('send', 'event', this.trackingCategory + ' | Extended Desc', 'Close | [P]', label);
                  }

                  this.props.clickDescriptionClose();
                }} />}
            </div>
          </div>
          {this.isClipbarRequired() && this.props.player.clipBarState.open &&
            <div className="player-clipbar">
              <AsyncClipBar />
            </div>
          }

          {(this.props.player.livestreamOverride || loadedVideo) && <CSSTransition
            classNames="react-fade-in"
            appear={true}
            timeout={{ enter: 250, leave: 0 }}
            in={true}>
            <div className="row hide-for-xlarge-up">
              <div className="small-12 columns">
                {
                  <AsyncSocialBar
                    baseUrl={this.getSocialUrl(loadedVideo)}
                    title={this.props.player.livestreamOverride ? this.props.show.livestream.service.title : loadedVideo && loadedVideo.title}
                    content={this.props.player.livestreamOverride ? this.props.show.livestream.service.description : loadedVideo && loadedVideo.shortDescription}
                    fb={{ tracking: { action: 'facebook/player' } }}
                    tw={{ tracking: { action: 'twitter/player' } }}
                    em={{ tracking: { action: 'email/player' } }}
                    embed={{ tracking: { action: 'embed/player' } }}
                    tpMediaId={loadedVideo && loadedVideo.tpMediaId}
                  />
                }
              </div>
            </div>
          </CSSTransition>}
        </div>
      </div>
    );
  }
}

export default connector(VideoPlayer);